import * as Sentry from '@sentry/nextjs';

const TOKEN_KEY = 'access-token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

/**
 * 获取token
 * 优先级：URL参数t → localStorage → sessionStorage → 内存备份
 */
export function getToken(): string | null {
  try {
    // 优先从URL参数获取（客户端WebView传入场景）
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('t');
      if (tokenFromUrl) {
        setToken(tokenFromUrl);
        return tokenFromUrl;
      }
    }

    // 从localStorage读取
    let token = localStorage.getItem(TOKEN_KEY);

    // 如果localStorage没有，尝试从sessionStorage
    if (!token) {
      token = sessionStorage.getItem(TOKEN_KEY);
      if (token) {
        Sentry.captureMessage('Token recovered from sessionStorage');
        localStorage.setItem(TOKEN_KEY, token);
      }
    }

    // 如果都没有，尝试内存备份
    if (!token && window._tokenBackup) {
      token = window._tokenBackup;
      localStorage.setItem(TOKEN_KEY, token);
      Sentry.captureMessage('Token recovered from memory backup');
    }

    return token;
  } catch (error) {
    Sentry.captureException(new Error(`Failed to get token: ${error}`));
    return null;
  }
}

/**
 * 设置token
 */
export function setToken(token: string): void {
  try {
    // localStorage
    localStorage.setItem(TOKEN_KEY, token);
    // sessionStorage作为备份
    sessionStorage.setItem(TOKEN_KEY, token);
    // 内存变量作为最后备份
    window._tokenBackup = token;
  } catch (error) {
    Sentry.captureException(new Error(`Failed to set token: ${error}`));
  }
}

/**
 * 清除token
 */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    window._tokenBackup = null;
  } catch (error) {
    console.error('Failed to clear token from localStorage:', error);
  }
}

/**
 * 检查是否有token
 */
export function hasToken(): boolean {
  return getToken() !== null;
}
